import {
  decodeWorkflowExport,
  encodeWorkflowExport,
  retargetWorkflowExport,
  retargetedFileName,
} from '@lg/core';
import { config } from '../config';

/**
 * Working D365 workflow exports from the template company, kept in public/workflow-templates
 * exactly as exported (UTF-16LE). The app saves them for the venue company with only the company
 * changed, ready for Workflows → Import there. Keyed by the workflow library's document type.
 */
export const D365_WORKFLOW_TEMPLATES: Readonly<
  Record<string, { file: string; from: string; calendar: string }>
> = {
  // sales-cd9 demo environment, USMF, exported 2026-10-23 (imports cleanly). Its due dates use work
  // calendar 24hr, which is company-specific: import the ledger package (it creates the calendar and
  // its working times) first, or the import reports "The calendar token does not exist".
  Journal: { file: 'Ledger daily journal workflow-1.1.0.0.xml', from: 'USMF', calendar: '24hr' },
};

export const templateUrl = (file: string): string =>
  `${import.meta.env.BASE_URL}workflow-templates/${encodeURIComponent(file)}`;

/** The template's workflow export for the company, as a file to save. */
export function d365WorkflowFile(
  file: string,
  bytes: Uint8Array,
  companyId: string,
  owner: string | null = null,
): { fileName: string; mimeType: string; bytes: Uint8Array } {
  const decoded = decodeWorkflowExport(bytes);
  return {
    fileName: retargetedFileName(file, companyId),
    mimeType: 'application/xml',
    bytes: encodeWorkflowExport(retargetWorkflowExport(decoded.xml, companyId, owner), decoded),
  };
}

export async function fetchD365WorkflowFile(documentType: string, companyId: string) {
  const template = D365_WORKFLOW_TEMPLATES[documentType];
  if (!template) throw new Error(`No D365 workflow export stored for ${documentType}`);
  const res = await fetch(templateUrl(template.file));
  if (!res.ok) throw new Error(`Could not load ${template.file} (${res.status})`);
  return d365WorkflowFile(
    template.file,
    new Uint8Array(await res.arrayBuffer()),
    companyId,
    config.envProfile.workflowOwner,
  );
}
